import { memo, useEffect, useState, useRef } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, Upload as CloudUploadIcon } from "components/icons";
import MDBox from "components/MDBox";
import { updateDocument, uploadDocument } from "app/services/knowledge.service";

const DOCUMENT_CATEGORIES = ["SOP", "POLICY", "TEMPLATE", "GUIDE", "ARTICLE", "OTHER"];
const DOCUMENT_STATUSES = ["DRAFT", "PENDING_REVIEW", "PUBLISHED", "ARCHIVED"];

const initialData = {
  id: null,
  documentTitle: "",
  description: "",
  category: "OTHER",
  status: "DRAFT",
  version: "1.0",
  tags: [],
  fileName: "",
};

const AddDocumentDialog = ({ open, setOpen, onSuccess, editObject, availableTags = [] }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editObject) {
      setData({ ...editObject });
      setSelectedFile(null);
    } else {
      setData(initialData);
      setSelectedFile(null);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.documentTitle || data.documentTitle.trim() === "") {
      setError("Document title is required");
      return false;
    }
    if (!editObject && !selectedFile) {
      setError("Please select a file to upload");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!data.documentTitle) {
        setData({ ...data, documentTitle: file.name.split(".")[0] });
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (editObject) {
        await updateDocument(data.id, data);
        setSuccess("Document updated successfully");
      } else {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("documentTitle", data.documentTitle);
        formData.append("description", data.description);
        formData.append("category", data.category);
        formData.append("status", data.status);
        formData.append("version", data.version);
        formData.append("tags", JSON.stringify(data.tags));
        
        await uploadDocument(formData);
        setSuccess("Document uploaded successfully");
      }
      setTimeout(() => {
        setData(initialData);
        setSelectedFile(null);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setSelectedFile(null);
    setMessageOpen(false);
    setOpen(false);
  };

  const handleTagToggle = (tag) => {
    const tagIds = data.tags.map(t => t.id || t);
    if (tagIds.includes(tag.id)) {
      setData({ ...data, tags: data.tags.filter(t => (t.id || t) !== tag.id) });
    } else {
      setData({ ...data, tags: [...data.tags, tag] });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Document" : "Upload Document"}</DialogTitle>
      <DialogContent>
        {!editObject && (
          <MDBox my={2}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              fullWidth
              sx={{ py: 2, borderStyle: "dashed" }}
            >
              {selectedFile ? selectedFile.name : "Click to select file"}
            </Button>
            {selectedFile && (
              <Typography variant="caption" color="text.secondary" mt={1}>
                Size: {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
            )}
          </MDBox>
        )}
        <MDBox my={2}>
          <TextField
            id="documentTitle"
            label="Document Title"
            fullWidth
            variant="outlined"
            value={data.documentTitle}
            onChange={(e) => setData({ ...data, documentTitle: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="category"
            label="Category"
            fullWidth
            select
            variant="outlined"
            value={data.category}
            onChange={(e) => setData({ ...data, category: e.target.value })}
          >
            {DOCUMENT_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          <TextField
            id="status"
            label="Status"
            fullWidth
            select
            variant="outlined"
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
          >
            {DOCUMENT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="version"
            label="Version"
            fullWidth
            variant="outlined"
            value={data.version}
            onChange={(e) => setData({ ...data, version: e.target.value })}
          />
        </MDBox>
        {availableTags.length > 0 && (
          <MDBox my={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>Tags</Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {availableTags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.tagName}
                  onClick={() => handleTagToggle(tag)}
                  color={(data.tags.map(t => t.id || t).includes(tag.id)) ? "primary" : "default"}
                  variant={(data.tags.map(t => t.id || t).includes(tag.id)) ? "filled" : "outlined"}
                />
              ))}
            </Box>
          </MDBox>
        )}
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Upload"}
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddDocumentDialog);

